/* AdminStuckOps — Stuck Operations Command Center
 *
 * Single page that surfaces stuck users + integrations in 6 tabs:
 *   Signup · Banking · Credit · Insurance · Shipping · Payments
 *
 * Every row has inline "Force-X" buttons wired to the existing override
 * endpoints (admin_controls / admin_shipping / admin_coverage etc.) so
 * admin can rescue stuck users in one click without leaving the page.
 *
 * Backend: /api/admin/stuck-ops/* + /api/admin/users/{id}/force-* + etc.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Loader2, RefreshCw, ShieldCheck, UserCheck, Banknote,
  CreditCard, Shield, Truck, DollarSign, ArrowRight, ExternalLink,
  CheckCircle2, XCircle, Clock, Wrench,
} from "lucide-react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";

const TABS = [
  { k: "signup",     label: "Signup",    icon: UserCheck,    color: "rose" },
  { k: "banking",    label: "Banking",   icon: Banknote,     color: "blue" },
  { k: "credit",     label: "Credit",    icon: CreditCard,   color: "amber" },
  { k: "insurance",  label: "Insurance", icon: Shield,       color: "emerald" },
  { k: "shipping",   label: "Shipping",  icon: Truck,        color: "violet" },
  { k: "payments",   label: "Payments",  icon: DollarSign,   color: "orange" },
];

const TONE = {
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  blue:    "bg-blue-50 text-blue-700 border-blue-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
  orange:  "bg-orange-50 text-orange-700 border-orange-200",
};

export default function AdminStuckOps() {
  const [tab, setTab] = useState("signup");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/stuck-ops/overview");
      setOverview(r.data);
    } catch {
      setOverview(null);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { loadOverview(); }, []);

  return (
    <div data-testid="admin-stuck-ops">
      <PageHeader
        title="Stuck Operations"
        subtitle="Live view of users and integrations stuck mid-flow across every rail. One-click force-actions."
        action={
          <button
            onClick={loadOverview}
            data-testid="stuck-ops-refresh"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border hover:bg-secondary text-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}/>
            Refresh
          </button>
        }
      />
      <div className="p-8 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stuck-ops-kpi-strip">
          <KpiTile
            label="Total stuck"
            value={overview?.total_stuck ?? "—"}
            icon={AlertTriangle}
            primary
            loading={loading}
          />
          {TABS.map(t => (
            <KpiTile
              key={t.k}
              label={t.label}
              value={overview?.categories?.[t.k] ?? "—"}
              icon={t.icon}
              tone={t.color}
              loading={loading}
              testid={`kpi-${t.k}`}
              onClick={() => setTab(t.k)}
              active={tab === t.k}
            />
          ))}
        </div>
        {/* Tab pills */}
        <div className="inline-flex rounded-full bg-secondary/40 p-0.5 gap-0.5 flex-wrap" data-testid="stuck-ops-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const count = overview?.categories?.[t.k] ?? 0;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`stuck-ops-tab-${t.k}`}
                className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 transition-colors ${
                  tab === t.k ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"
                }`}
              >
                <Icon className="w-3 h-3"/>
                {t.label}
                {count > 0 && (
                  <span className={`ml-1 text-[10px] px-1.5 rounded-full ${tab === t.k ? "bg-background/20" : "bg-foreground/10"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* Active panel */}
        <div className="rounded-2xl border border-border bg-white">
          {tab === "signup"    && <SignupPanel onAction={loadOverview}/>}
          {tab === "banking"   && <BankingPanel onAction={loadOverview}/>}
          {tab === "credit"    && <CreditPanel onAction={loadOverview}/>}
          {tab === "insurance" && <InsurancePanel onAction={loadOverview}/>}
          {tab === "shipping"  && <ShippingPanel onAction={loadOverview}/>}
          {tab === "payments"  && <PaymentsPanel onAction={loadOverview}/>}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone, primary, active, loading, testid, onClick }) {
  const klass = primary
    ? "bg-foreground text-background"
    : `bg-white ${active ? "border-foreground ring-1 ring-foreground/20" : "border-border"} border`;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      disabled={!onClick}
      className={`text-left rounded-xl p-3 transition-all ${klass} ${onClick ? "hover:shadow-md cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[10px] mono uppercase tracking-widest ${primary ? "text-accent" : "text-foreground/55"}`}>
          {label}
        </span>
        {Icon && <Icon className={`w-3.5 h-3.5 ${primary ? "text-accent/70" : "text-foreground/30"}`}/>}
      </div>
      <div className="display text-2xl mt-1.5 tabular-nums">
        {loading ? <Loader2 className="w-4 h-4 animate-spin inline opacity-50"/> : value}
      </div>
      {tone && !primary && value > 0 && (
        <div className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded-full border ${TONE[tone]}`}>
          Action needed
        </div>
      )}
    </button>
  );
}

// ─── SIGNUP ────────────────────────────────────────────────────────────
function SignupPanel({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stuckHours, setStuckHours] = useState(24);
  const [acting, setActing] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(`/admin/stuck-ops/signup?stuck_hours=${stuckHours}`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [stuckHours]);

  const forceAdvance = async (uid, step) => {
    const reason = prompt(`Force-advance step "${step}"? Please type a reason (min 3 chars):`);
    if (!reason || reason.length < 3) return;
    setActing(`adv-${uid}`);
    try {
      await api.post(`/admin/users/${uid}/force-advance-step`, { step, reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Force-advance failed");
    } finally { setActing(null); }
  };

  const forceComplete = async (uid) => {
    const reason = prompt("Force-COMPLETE onboarding for this user? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`cpl-${uid}`);
    try {
      await api.post(`/admin/users/${uid}/force-complete-onboarding`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Force-complete failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5 space-y-4" data-testid="panel-signup">
      <FilterBar>
        <span className="text-xs text-foreground/60">Stuck for at least</span>
        <select
          value={stuckHours}
          onChange={e => setStuckHours(Number(e.target.value))}
          data-testid="signup-hours-filter"
          className="h-8 rounded-md border border-border px-2 text-xs bg-white"
        >
          {[1, 6, 12, 24, 48, 72, 168].map(h => (
            <option key={h} value={h}>{h < 24 ? `${h}h` : `${h / 24}d`}</option>
          ))}
        </select>
      </FilterBar>
      <Table loading={loading} empty={items.length === 0} emptyHint="Nobody is stuck right now 🎉">
        <thead><tr>
          <Th>User</Th><Th>Stuck step</Th><Th>Age</Th><Th>KYB</Th><Th>Email</Th>
          <Th className="text-right">Actions</Th>
        </tr></thead>
        <tbody>
          {items.map(u => (
            <tr key={u.user_id} data-testid={`signup-row-${u.user_id}`} className="border-t border-border">
              <Td>
                <div className="font-medium text-[13px] truncate">{u.company || u.name || u.email}</div>
                <div className="text-[11px] text-foreground/55 truncate">{u.email} · {u.role}</div>
              </Td>
              <Td>
                <span className="inline-block text-[10px] px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 uppercase tracking-widest mono">
                  {u.stuck_step?.replace(/_/g, " ")}
                </span>
              </Td>
              <Td><span className="text-xs tabular-nums">{u.age_hours}h</span></Td>
              <Td>
                <Pill status={u.kyc_status}/>
              </Td>
              <Td>{u.email_verified ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> : <XCircle className="w-3.5 h-3.5 text-rose-500"/>}</Td>
              <Td className="text-right">
                <div className="inline-flex gap-1.5">
                  <SmallBtn
                    onClick={() => forceAdvance(u.user_id, u.stuck_step)}
                    loading={acting === `adv-${u.user_id}`}
                    testid={`force-advance-${u.user_id}`}
                    label="Advance step"
                  />
                  <SmallBtn
                    onClick={() => forceComplete(u.user_id)}
                    loading={acting === `cpl-${u.user_id}`}
                    testid={`force-complete-${u.user_id}`}
                    label="Complete"
                    variant="primary"
                  />
                  <button onClick={() => nav(`/admin/users?id=${u.user_id}`)}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md hover:bg-secondary text-[11px] text-foreground/65">
                    Open <ArrowRight className="w-3 h-3"/>
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── BANKING ───────────────────────────────────────────────────────────
function BankingPanel({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/stuck-ops/banking")
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const forceRelink = async (itemId) => {
    const reason = prompt("Force Plaid re-link for this item? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(itemId);
    try {
      await api.post(`/admin/integrations/plaid/links/${itemId}/force-relink`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Force-relink failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5" data-testid="panel-banking">
      <Table loading={loading} empty={items.length === 0} emptyHint="No banking issues right now.">
        <thead><tr>
          <Th>User</Th><Th>Item</Th><Th>Status</Th><Th>Updated</Th>
          <Th className="text-right">Actions</Th>
        </tr></thead>
        <tbody>
          {items.map(i => (
            <tr key={i.id || i.item_id} data-testid={`banking-row-${i.id || i.item_id}`} className="border-t border-border">
              <Td>
                <div className="text-[13px] font-medium truncate">{i.user_company || i.user_email || "—"}</div>
                <div className="text-[10px] mono text-foreground/45">{i.user_id?.slice(0, 8)}</div>
              </Td>
              <Td><span className="text-[11px] mono">{(i.item_id || i.id || "—").slice(0, 18)}</span></Td>
              <Td><Pill status={i.status}/></Td>
              <Td><span className="text-[11px] text-foreground/55">{(i.updated_at || i.created_at || "").slice(0, 10)}</span></Td>
              <Td className="text-right">
                <SmallBtn
                  onClick={() => forceRelink(i.item_id || i.id)}
                  loading={acting === (i.item_id || i.id)}
                  testid={`force-relink-${i.item_id || i.id}`}
                  label="Force re-link"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── CREDIT ────────────────────────────────────────────────────────────
function CreditPanel({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/stuck-ops/credit")
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const decide = async (aid, status) => {
    const reason = prompt(`${status === "approved" ? "APPROVE" : "DENY"} this credit application? Reason (min 3 chars):`);
    if (!reason || reason.length < 3) return;
    let limit;
    if (status === "approved") {
      const lim = prompt("Approved credit limit ($):", "10000");
      if (lim === null) return;
      limit = Number(lim) || 0;
    }
    setActing(aid);
    try {
      await api.post(`/admin/credit-apps/${aid}/decision`, { status, reason, limit });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Decision failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5" data-testid="panel-credit">
      <Table loading={loading} empty={items.length === 0} emptyHint="No credit apps stuck.">
        <thead><tr>
          <Th>Buyer</Th><Th>Status</Th><Th>Created</Th>
          <Th className="text-right">Actions</Th>
        </tr></thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id} data-testid={`credit-row-${a.id}`} className="border-t border-border">
              <Td>
                <div className="text-[13px] font-medium truncate">{a.buyer_company || a.buyer_email || "—"}</div>
                <div className="text-[10px] mono text-foreground/45">{a.id?.slice(0, 8)}</div>
              </Td>
              <Td><Pill status={a.status}/></Td>
              <Td><span className="text-[11px] text-foreground/55">{(a.created_at || "").slice(0, 10)}</span></Td>
              <Td className="text-right">
                <div className="inline-flex gap-1.5">
                  <SmallBtn
                    onClick={() => decide(a.id, "approved")}
                    loading={acting === a.id}
                    testid={`credit-approve-${a.id}`}
                    label="Approve"
                    variant="primary"
                  />
                  <SmallBtn
                    onClick={() => decide(a.id, "denied")}
                    testid={`credit-deny-${a.id}`}
                    label="Deny"
                    variant="danger"
                  />
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── INSURANCE ─────────────────────────────────────────────────────────
function InsurancePanel({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/stuck-ops/insurance")
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const revoke = async (coverId) => {
    const reason = prompt("Revoke this insurance cover? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(coverId);
    try {
      await api.post(`/admin/buyer-covers/${coverId}/revoke`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Revoke failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5" data-testid="panel-insurance">
      <Table loading={loading} empty={items.length === 0} emptyHint="No insurance issues.">
        <thead><tr>
          <Th>Buyer</Th><Th>Limit</Th><Th>Status</Th><Th>Created</Th>
          <Th className="text-right">Actions</Th>
        </tr></thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} data-testid={`insurance-row-${c.id}`} className="border-t border-border">
              <Td>
                <div className="text-[13px] font-medium truncate">{c.buyer_company || c.buyer_email || "—"}</div>
                <div className="text-[10px] mono text-foreground/45">{c.id?.slice(0, 8)}</div>
              </Td>
              <Td><span className="text-xs tabular-nums">${(c.permanent_limit || 0).toLocaleString()}</span></Td>
              <Td><Pill status={c.status}/></Td>
              <Td><span className="text-[11px] text-foreground/55">{(c.created_at || "").slice(0, 10)}</span></Td>
              <Td className="text-right">
                <SmallBtn
                  onClick={() => revoke(c.id)}
                  loading={acting === c.id}
                  testid={`insurance-revoke-${c.id}`}
                  label="Revoke"
                  variant="danger"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── SHIPPING (Verqo Shipping + Warp) ──────────────────────────────────────────
function ShippingPanel({ onAction }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("");
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    const url = provider
      ? `/admin/stuck-ops/shipping?provider=${provider}`
      : "/admin/stuck-ops/shipping";
    api.get(url)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [provider]);

  const rebook = async (sid) => {
    const reason = prompt("Force-rebook this shipment? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`rb-${sid}`);
    try {
      await api.post(`/admin/shipping/${sid}/force-rebook`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Rebook failed");
    } finally { setActing(null); }
  };

  const warpRequote = async (sid) => {
    const reason = prompt("Force a Warp quote refresh? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`wq-${sid}`);
    try {
      const r = await api.post(`/admin/integrations/warp/shipments/${sid}/force-quote-refresh`, { reason });
      alert(`Warp re-quote applied (mode=${r.data?.warp_mode}).`);
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Warp re-quote failed");
    } finally { setActing(null); }
  };

  const warpCancel = async (sid) => {
    const reason = prompt("Force-cancel this Warp shipment? (queues a support email to Warp). Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`wc-${sid}`);
    try {
      await api.post(`/admin/integrations/warp/shipments/${sid}/force-cancel`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Warp cancel failed");
    } finally { setActing(null); }
  };

  const markDelivered = async (sid) => {
    const reason = prompt("Mark shipment delivered manually? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`md-${sid}`);
    try {
      await api.post(`/admin/shipping/${sid}/mark-delivered`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Mark-delivered failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5 space-y-4" data-testid="panel-shipping">
      <FilterBar>
        <span className="text-xs text-foreground/60">Provider</span>
        <select
          value={provider}
          onChange={e => setProvider(e.target.value)}
          data-testid="shipping-provider-filter"
          className="h-8 rounded-md border border-border px-2 text-xs bg-white"
        >
          <option value="">All</option>
          <option value="verqo-shipping">Verqo Shipping (parcel)</option>
          <option value="warp">Warp (freight)</option>
        </select>
      </FilterBar>
      <Table loading={loading} empty={items.length === 0} emptyHint="No stuck shipments.">
        <thead><tr>
          <Th>Shipment</Th><Th>Provider</Th><Th>Status</Th><Th>Carrier</Th>
          <Th className="text-right">Actions</Th>
        </tr></thead>
        <tbody>
          {items.map(s => (
            <tr key={s.id} data-testid={`shipping-row-${s.id}`} className="border-t border-border">
              <Td>
                <div className="text-[11px] mono">{(s.id || "").slice(0, 8)}</div>
                <div className="text-[10px] text-foreground/45">{(s.tracking_number || s.tracking || "—").slice(0, 30)}</div>
              </Td>
              <Td><Pill status={s.provider || "none"}/></Td>
              <Td><Pill status={s.status || s.label_status || "—"}/></Td>
              <Td><span className="text-[11px] text-foreground/60">{s.carrier || "—"}</span></Td>
              <Td className="text-right">
                <div className="inline-flex gap-1.5 flex-wrap justify-end">
                  <SmallBtn
                    onClick={() => rebook(s.id)}
                    loading={acting === `rb-${s.id}`}
                    testid={`ship-rebook-${s.id}`}
                    label="Rebook"
                  />
                  <SmallBtn
                    onClick={() => markDelivered(s.id)}
                    loading={acting === `md-${s.id}`}
                    testid={`ship-deliver-${s.id}`}
                    label="Deliver"
                  />
                  {s.provider === "warp" && (
                    <>
                      <SmallBtn
                        onClick={() => warpRequote(s.id)}
                        loading={acting === `wq-${s.id}`}
                        testid={`warp-requote-${s.id}`}
                        label="Re-quote (Warp)"
                      />
                      <SmallBtn
                        onClick={() => warpCancel(s.id)}
                        loading={acting === `wc-${s.id}`}
                        testid={`warp-cancel-${s.id}`}
                        label="Cancel"
                        variant="danger"
                      />
                    </>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── PAYMENTS ──────────────────────────────────────────────────────────
function PaymentsPanel({ onAction }) {
  const [data, setData] = useState({ stuck_txns: [], overdue_invoices: [] });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/stuck-ops/payments")
      .then(r => setData(r.data || { stuck_txns: [], overdue_invoices: [] }))
      .catch(() => setData({ stuck_txns: [], overdue_invoices: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const pollStatus = async (txnId) => {
    setActing(`poll-${txnId}`);
    try {
      await api.post(`/admin/payments-ops/transactions/${txnId}/poll-status`);
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Poll failed");
    } finally { setActing(null); }
  };

  const retryRail = async (kind, id) => {
    const rail = prompt("Retry on which rail? card | ach | rtp | wire | wallet", "ach");
    if (!rail) return;
    const reason = prompt("Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`retry-${id}`);
    try {
      const url = kind === "invoice"
        ? `/admin/invoices/${id}/retry-with-rail`
        : `/admin/wallet-txns/${id}/retry-with-rail`;
      const r = await api.post(url, { rail, reason });
      alert(`Retry created — dispatch=${r.data?.dispatch_status}`);
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Retry failed");
    } finally { setActing(null); }
  };

  const forcePaid = async (iid) => {
    const reason = prompt("Force-mark invoice as PAID? Reason (min 3 chars):");
    if (!reason || reason.length < 3) return;
    setActing(`fp-${iid}`);
    try {
      await api.post(`/admin/invoices/${iid}/force-paid`, { reason });
      load(); onAction?.();
    } catch (err) {
      alert(err?.response?.data?.detail || "Force-paid failed");
    } finally { setActing(null); }
  };

  return (
    <div className="p-5 space-y-6" data-testid="panel-payments">
      <div>
        <h3 className="display text-sm mb-2">Stuck transactions ({data.stuck_txn_count || 0})</h3>
        <Table loading={loading} empty={(data.stuck_txns || []).length === 0} emptyHint="No stuck transactions.">
          <thead><tr>
            <Th>User</Th><Th>Amount</Th><Th>Method</Th><Th>Status</Th><Th>Age</Th>
            <Th className="text-right">Actions</Th>
          </tr></thead>
          <tbody>
            {(data.stuck_txns || []).map(t => (
              <tr key={t.id} data-testid={`payment-txn-${t.id}`} className="border-t border-border">
                <Td>
                  <div className="text-[13px] truncate">{t.user_company || t.user_email || "—"}</div>
                </Td>
                <Td><span className="tabular-nums text-xs">${Math.abs(t.amount || 0).toFixed(2)}</span></Td>
                <Td><span className="text-[11px]">{t.method || t.rail || "—"}</span></Td>
                <Td><Pill status={t.status}/></Td>
                <Td><span className="text-[11px] text-foreground/55">{(t.created_at || "").slice(0, 10)}</span></Td>
                <Td className="text-right">
                  <div className="inline-flex gap-1.5">
                    <SmallBtn
                      onClick={() => pollStatus(t.id)}
                      loading={acting === `poll-${t.id}`}
                      testid={`payment-poll-${t.id}`}
                      label="Poll status"
                    />
                    <SmallBtn
                      onClick={() => retryRail("txn", t.id)}
                      loading={acting === `retry-${t.id}`}
                      testid={`payment-retry-${t.id}`}
                      label="Retry rail"
                      variant="primary"
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div>
        <h3 className="display text-sm mb-2">Overdue invoices ({data.overdue_invoice_count || 0})</h3>
        <Table loading={loading} empty={(data.overdue_invoices || []).length === 0} emptyHint="No overdue invoices.">
          <thead><tr>
            <Th>Invoice</Th><Th>Amount</Th><Th>Due</Th><Th>Status</Th>
            <Th className="text-right">Actions</Th>
          </tr></thead>
          <tbody>
            {(data.overdue_invoices || []).map(i => (
              <tr key={i.id} data-testid={`payment-inv-${i.id}`} className="border-t border-border">
                <Td>
                  <div className="text-[11px] mono">{i.code || (i.id || "").slice(0, 8)}</div>
                </Td>
                <Td><span className="tabular-nums text-xs">${(i.amount || 0).toFixed(2)}</span></Td>
                <Td><span className="text-[11px] text-foreground/55">{(i.due_date || "").slice(0, 10)}</span></Td>
                <Td><Pill status={i.status}/></Td>
                <Td className="text-right">
                  <div className="inline-flex gap-1.5">
                    <SmallBtn
                      onClick={() => retryRail("invoice", i.id)}
                      loading={acting === `retry-${i.id}`}
                      testid={`invoice-retry-${i.id}`}
                      label="Retry rail"
                    />
                    <SmallBtn
                      onClick={() => forcePaid(i.id)}
                      loading={acting === `fp-${i.id}`}
                      testid={`invoice-force-paid-${i.id}`}
                      label="Force paid"
                      variant="primary"
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────────
function FilterBar({ children }) {
  return <div className="inline-flex items-center gap-2 rounded-full bg-secondary/30 px-3 py-1.5 border border-border">{children}</div>;
}

function Table({ loading, empty, emptyHint, children }) {
  if (loading) {
    return (
      <div className="py-12 text-center text-xs text-foreground/55 inline-flex items-center gap-2 justify-center w-full">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading…
      </div>
    );
  }
  if (empty) {
    return <div className="py-10 text-center text-sm text-foreground/55" data-testid="table-empty">{emptyHint}</div>;
  }
  return <table className="w-full text-sm">{children}</table>;
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-[10px] mono uppercase tracking-widest text-foreground/55 font-normal py-2 px-2 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`py-2 px-2 align-middle ${className}`}>{children}</td>;
}

function Pill({ status }) {
  const STYLE = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    transit: "bg-blue-50 text-blue-700 border-blue-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    processing: "bg-amber-50 text-amber-800 border-amber-200",
    initiated: "bg-amber-50 text-amber-800 border-amber-200",
    submitted: "bg-amber-50 text-amber-800 border-amber-200",
    in_flight: "bg-amber-50 text-amber-800 border-amber-200",
    under_review: "bg-amber-50 text-amber-800 border-amber-200",
    refused: "bg-rose-50 text-rose-700 border-rose-200",
    revoked: "bg-rose-50 text-rose-700 border-rose-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
    error: "bg-rose-50 text-rose-700 border-rose-200",
    expired: "bg-rose-50 text-rose-700 border-rose-200",
    reauth_required: "bg-rose-50 text-rose-700 border-rose-200",
    ITEM_LOGIN_REQUIRED: "bg-rose-50 text-rose-700 border-rose-200",
    cancelled: "bg-secondary text-foreground/55 border-border",
  };
  const cls = STYLE[status] || "bg-secondary text-foreground/65 border-border";
  return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border mono uppercase tracking-widest ${cls}`}>{status || "—"}</span>;
}

function SmallBtn({ onClick, loading, label, variant, testid }) {
  const base = "inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors disabled:opacity-50";
  const v = {
    primary: "bg-foreground text-background hover:bg-foreground/90",
    danger:  "bg-rose-600 text-white hover:bg-rose-700",
  }[variant] || "border border-border hover:bg-secondary text-foreground/80";
  return (
    <button onClick={onClick} disabled={loading} data-testid={testid} className={`${base} ${v}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Wrench className="w-3 h-3"/>}
      {label}
    </button>
  );
}
